export const REASON_OPTIONS = [] as const;

export type ReasonCode = string;
export type ReasonCategoryCode =
  | 'hand_check'
  | 'meaningless_work'
  | 'fake_task'
  | 'camera_anomaly'
  | 'other'
  | 'legacy';
export interface ReasonOption {
  code: ReasonCode;
  category_code: ReasonCategoryCode;
  category_label?: string;
  category_sort_order?: number;
  label: string;
  enabled: boolean;
  selectable: boolean;
  sort_order: number;
  taxonomy_version: number;
}

export interface ReasonCategory {
  code: ReasonCategoryCode;
  label: string;
  enabled: boolean;
  sort_order: number;
  taxonomy_version: number;
  options: ReasonOption[];
}
export type ReasonTaskStatus =
  | 'pending'
  | 'in_progress'
  | 'completed_editable'
  | 'completed_locked';
export type ReasonTaskViewStatus = 'draft' | 'editable' | 'read_only';
export type ReasonMyTaskSort =
  | 'assigned_at'
  | 'committed_at'
  | 'edit_until'
  | 'output_duration'
  | 'episode_id';
export type ReasonMyTaskSortOrder = 'asc' | 'desc';
export type DdbSyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';

export interface ReasonSegment {
  segment_index: number;
  start_frame: number;
  end_frame_exclusive: number;
  start_time_s: number;
  end_time_s: number;
  reason_code: ReasonCode | null;
  reason_note?: string | null;
  reason_category_code?: ReasonCategoryCode | null;
  taxonomy_version?: number | null;
  committed_reason_code?: ReasonCode | null;
  committed_reason_note?: string | null;
  committed_reason_category_code?: ReasonCategoryCode | null;
  committed_taxonomy_version?: number | null;
  reason_codes?: ReasonCode[];
  reason_notes?: Partial<Record<ReasonCode, string>>;
  committed_reason_codes?: ReasonCode[];
  committed_reason_notes?: Partial<Record<ReasonCode, string>>;
}

export interface ReasonTaskSummary {
  episode_id: string;
  status: ReasonTaskStatus;
  assigned_operator: string | null;
  assigned_at_ms: number | null;
  first_committed_at_ms: number | null;
  edit_until_ms: number | null;
  fail_segment_count: number;
  completed_segment_count: number;
  source_audit_hash: string;
  revision: number;
  last_committed_revision?: number | null;
  ddb_sync_status: DdbSyncStatus;
  reason_taxonomy_status?: 'unlabeled' | 'v1_legacy' | 'v2' | 'mixed';
  preview_url?: string;
  annotation_schema_version?: number;
  segment_contract_version?: number;
  output_duration_s?: number;
}

export interface ReasonTask extends ReasonTaskSummary {
  duration_s: number;
  observed_media_duration_s?: number | null;
  annotation_fps: number;
  segments: ReasonSegment[];
}

export function effectiveReasonMediaDuration(
  task: Pick<ReasonTask, 'duration_s' | 'observed_media_duration_s'>,
  observedMediaDurationS?: number | null
): number {
  const observed = observedMediaDurationS ?? task.observed_media_duration_s;
  return observed != null && Number.isFinite(observed) && observed > 0
    ? Math.min(task.duration_s, observed)
    : task.duration_s;
}

export interface ReasonAdminTask extends ReasonTaskSummary {
  source_changed?: boolean;
  ddb_sync_error?: string | null;
}

export interface ReasonResultSummary {
  episode_id: string;
  operator: string;
  revision: number;
  completed_at_ms: number;
  edit_until_ms: number;
  handling_elapsed_ms: number;
  environment: string;
  worker_version: string;
  submission_version: number;
  segment_count: number;
  reason_selection_count: number;
  output_duration_s: number;
  source_audit_hash: string;
  current_source_audit_hash: string;
  source_changed: boolean;
  ddb_sync_status: DdbSyncStatus;
}

export interface ReasonResultSelection {
  category_code: string | null;
  reason_code: string;
  reason_label: string;
  reason_note: string | null;
  taxonomy_version: number;
  enabled_now: boolean;
}

export interface ReasonResultSegment {
  segment_index: number;
  start_frame: number;
  end_frame_exclusive: number;
  start_time_s: number;
  end_time_s: number;
  reasons: ReasonResultSelection[];
}

export interface ReasonResultRevision {
  revision: number;
  operator: string;
  completed_at_ms: number;
  submission_version: number;
}

export interface ReasonResultDetail extends ReasonResultSummary {
  duration_s: number;
  observed_media_duration_s: number | null;
  annotation_fps: number;
  segments: ReasonResultSegment[];
  revisions: ReasonResultRevision[];
}

export interface ReasonResultListResponse {
  results: ReasonResultSummary[];
  next_cursor: string | null;
}

export interface ReasonResultListOptions {
  episode_id?: string;
  operator?: string;
  from_ms?: number;
  to_ms?: number;
  cursor?: string | null;
  limit?: number;
}

export interface ReasonLeaderboardRow {
  operator: string;
  completed_tasks: number;
  labeled_segments: number;
  completion_duration_ms: number;
  handling_elapsed_ms: number;
  output_duration_s: number;
  category_distribution: Record<string, number>;
  reason_distribution: Record<string, number>;
}

export interface ReasonLeaderboardData {
  completed_tasks: number;
  labeled_segments: number;
  reason_selections: number;
  output_duration_s: number;
  categories: ReasonCategory[];
  rows: ReasonLeaderboardRow[];
}

export interface ReasonWorkResultsExportPart {
  part: number;
  segment_count: number;
  filename: string;
  token: string;
}

export interface ReasonWorkResultsExportPlan {
  environment: string;
  generated_at_ms: number;
  expires_at_ms: number;
  snapshot_to_ms: number;
  from_ms: number | null;
  to_ms: number;
  episode_count: number;
  segment_count: number;
  selection_count: number;
  max_segments_per_file: number;
  parts: ReasonWorkResultsExportPart[];
}

export interface ReasonExportErrorBody {
  code: string;
  detail: string;
  segment_count?: number;
  max_segments?: number;
}

export interface ReasonStats {
  pending_tasks: number;
  claimable_pending_tasks: number;
  blocked_pending_tasks: number;
  assigned_drafts: number;
  completed_tasks: number;
  editable_tasks: number;
  locked_tasks: number;
  labeled_fail_segments: number;
  reason_distribution: Record<string, number>;
  category_distribution: Record<string, number>;
  last_source_ingestion_at_ms: number | null;
  last_ingestion_error: string | null;
}

export interface ReasonTaskListResponse {
  tasks: ReasonTaskSummary[];
  summary: ReasonMyTasksSummary;
  next_cursor: string | null;
}

export interface ReasonMyTasksSummary {
  assigned_episodes: number;
  completed_episodes: number;
  labeled_segments: number;
  output_duration_s: number;
}

export interface ReasonMyTaskListOptions {
  status?: ReasonTaskViewStatus;
  query?: string;
  sort?: ReasonMyTaskSort;
  order?: ReasonMyTaskSortOrder;
  cursor?: string | null;
  limit?: number;
}

export interface ReasonTaskResponse {
  task: ReasonTask;
}

export interface ReasonAdminTaskListResponse {
  tasks: ReasonAdminTask[];
  has_more: boolean;
  next_offset: number | null;
}

export interface ReasonSourceRefreshResponse {
  task: ReasonAdminTask;
  previous_source_audit_hash: string;
  source_audit_hash: string;
  previous_fail_segment_count: number;
  fail_segment_count: number;
  preserved_reason_count: number;
  requires_relabel: boolean;
}

export interface ReasonSourceDiagnostic {
  ok: true;
  episode_id: string;
  task: {
    source_audit_hash: string;
    duration_s: number;
    annotation_fps: number;
    num_frames: number;
    source_changed: boolean;
    revision: number;
    last_committed_revision: number | null;
    ddb_sync_status: DdbSyncStatus;
    ddb_sync_error: string | null;
  };
  ddb: {
    seccap_done: boolean | null;
    source_audit_hash: string | null;
    source_bitmap_length: number | null;
    duration_s: number | null;
    annotation_fps: number | null;
    reason_revision: number | null;
  };
  checks: {
    source_complete: boolean;
    source_audit_hash_matches: boolean;
    duration_matches: boolean;
    annotation_fps_matches: boolean;
    reason_revision_writable: boolean;
    write_condition_ready: boolean;
  };
}

export interface ReasonIngestionResult {
  ok: boolean;
  enabled: boolean;
  dry_run: boolean;
  candidates: number;
  ingestible: number;
  not_sampled: number;
  zero_fail: number;
  invalid_source: number;
  inserted: number;
  refreshed: number;
  unchanged: number;
  source_changed: number;
  quarantined: number;
  ddb_reconciled: number;
  ddb_source_drift: number;
  ddb_reconciliation_failed: number;
  pages?: number;
  complete?: boolean;
  error?: string;
}

export interface ReasonLeaderboardResponse {
  leaderboard: ReasonLeaderboardData;
}

export interface ReasonStatsResponse {
  stats: ReasonStats;
}

export interface PatchReasonSegmentInput {
  reason_codes: ReasonCode[];
  reason_notes?: Partial<Record<ReasonCode, string>>;
  expected_revision: number;
}

export interface OwnerFullSnapshotSegment {
  segment_index: number;
  start_frame: number;
  end_frame_exclusive: number;
  reason_codes: ReasonCode[];
  reason_notes?: Partial<Record<ReasonCode, string>>;
}

export interface CommitReasonTaskInput {
  expected_revision?: number;
  based_on_revision?: number;
  allow_draft_overwrite?: true;
  source_audit_hash: string;
  segment_contract_version?: number;
  segments?: OwnerFullSnapshotSegment[];
  observed_media_duration_s?: number;
}

export interface CommitAndClaimNextReasonTaskInput extends CommitReasonTaskInput {
  operation_id: string;
}

export interface CommitAndClaimNextReasonTaskResult {
  committed: true;
  task: ReasonTask;
  next_task: ReasonTask | null;
  queue_state: 'claimed' | 'empty';
  receipt: ReasonSubmissionReceipt;
}

export interface ReasonRuntimeInfo {
  flow: 'seccapreason';
  environment: string;
  worker_version: string;
  segment_contract_version: number;
}

export interface ReasonSubmissionReceipt {
  environment: string;
  worker_version: string;
  episode_id: string;
  operator: string;
  revision: number;
  completed_at_ms: number;
  ddb_sync_status: DdbSyncStatus;
  commit_metadata?: {
    commit_mode: 'owner_full_snapshot';
    based_on_revision: number;
    overwritten_revision: number;
    overwritten_draft: boolean;
  };
}

export interface ReasonSubmissionHistoryEntry extends ReasonSubmissionReceipt {
  base_revision: number;
  source_audit_hash: string;
  edit_until_ms: number;
  handling_elapsed_ms: number;
  is_first_completion: boolean;
  submission_version: number;
  segment_count: number;
  reason_selection_count: number;
}

export interface ReasonSegmentContractV2Result {
  dry_run: boolean;
  inspected: number;
  unassigned_rebuildable: number;
  assigned_empty_rebuildable: number;
  partial_rebuildable: number;
  partial_conflict: number;
  partial_legacy: number;
  completed_unchanged: number;
  rebuilt: number;
  zero_duration_segments_removed: number;
  zero_fail_blocked: number;
  remaining_legacy: number;
  rows: Array<{
    episode_id: string;
    disposition:
      | 'unassigned_rebuildable'
      | 'assigned_empty_rebuildable'
      | 'partial_rebuildable'
      | 'partial_conflict'
      | 'partial_legacy'
      | 'completed_unchanged'
      | 'zero_fail_blocked';
    before_segment_count: number;
    after_segment_count: number;
    preserved_reason_count?: number;
    dropped_labeled_segment_count?: number;
    revision?: number;
  }>;
}

export interface ReasonSegmentContractV2Options {
  include_assigned_empty?: boolean;
  include_partial_legacy?: boolean;
  episode_id?: string;
  expected_revision?: number;
}

export type ReasonClientEvent =
  | 'auth_refresh_started'
  | 'auth_refresh_succeeded'
  | 'auth_refresh_failed'
  | 'api_401'
  | 'api_403'
  | 'api_5xx'
  | 'network_error'
  | 'editor_runtime_error'
  | 'task_restore_started'
  | 'task_restore_succeeded'
  | 'task_restore_failed';

export interface ReasonClientEventInput {
  event: ReasonClientEvent;
  episode_id?: string;
  revision?: number;
  segment_index?: number;
  pending_save_count?: number;
  http_status?: number;
  error_code?: string;
  visibility_state?: DocumentVisibilityState;
}

export interface SecCapReasonApiClient {
  claimNext(): Promise<ReasonTask | null>;
  listMyTasks(options?: ReasonMyTaskListOptions): Promise<ReasonTaskListResponse>;
  getTask(episodeId: string): Promise<ReasonTask>;
  patchSegment(
    episodeId: string,
    segmentIndex: number,
    input: PatchReasonSegmentInput
  ): Promise<ReasonTask>;
  commitTask(episodeId: string, input: CommitReasonTaskInput): Promise<ReasonTask>;
  commitAndClaimNext(
    episodeId: string,
    input: CommitAndClaimNextReasonTaskInput
  ): Promise<CommitAndClaimNextReasonTaskResult>;
  listAdminTasks(
    offset?: number,
    limit?: number,
    query?: string
  ): Promise<ReasonAdminTaskListResponse>;
  listSubmissionHistory(episodeId: string): Promise<ReasonSubmissionHistoryEntry[]>;
  listResults(options: ReasonResultListOptions): Promise<ReasonResultListResponse>;
  getResult(episodeId: string, revision?: number): Promise<ReasonResultDetail>;
  getRuntimeInfo(): Promise<ReasonRuntimeInfo>;
  migrateSegmentContractV2(
    dryRun: boolean,
    limit: number,
    options?: ReasonSegmentContractV2Options
  ): Promise<ReasonSegmentContractV2Result>;
  reassignTask(episodeId: string, operator: string): Promise<ReasonAdminTask>;
  retrySync(episodeId: string): Promise<ReasonAdminTask>;
  refreshSource(episodeId: string, expectedSourceAuditHash: string): Promise<ReasonAdminTask>;
  diagnoseSource(episodeId: string): Promise<ReasonSourceDiagnostic>;
  runIngestion(
    dryRun: boolean,
    maxPages: number,
    fromBeginning?: boolean
  ): Promise<ReasonIngestionResult>;
  getStats(): Promise<ReasonStats>;
  recordClientEvent(input: ReasonClientEventInput): Promise<void>;
  listReasonOptions(): Promise<ReasonOption[]>;
  getLeaderboard(): Promise<ReasonLeaderboardData>;
}

export function getReasonTaskViewStatus(
  task: Pick<ReasonTaskSummary, 'first_committed_at_ms' | 'edit_until_ms' | 'status'>,
  nowMs = Date.now()
): ReasonTaskViewStatus {
  if (task.first_committed_at_ms == null) return 'draft';
  if (task.edit_until_ms != null && nowMs < task.edit_until_ms) return 'editable';
  return 'read_only';
}

export function isReasonTaskLocked(
  task: Pick<ReasonTaskSummary, 'first_committed_at_ms' | 'edit_until_ms' | 'status'>,
  nowMs = Date.now()
): boolean {
  return getReasonTaskViewStatus(task, nowMs) === 'read_only';
}

export const OTHER_REASON_CODE = 'other';
export const MAX_REASON_NOTE_CODE_POINTS = 200;

export function countUnicodeCodePoints(value: string): number {
  return Array.from(value).length;
}

export function canonicalizeReasonNote(value: string): string {
  return value.normalize('NFC').trim();
}

export function hasVisibleReasonNoteContent(value: string): boolean {
  return /[\p{L}\p{N}\p{S}]/u.test(value);
}

export function truncateUnicodeCodePoints(
  value: string,
  maxCodePoints = MAX_REASON_NOTE_CODE_POINTS
): string {
  const normalized = value.normalize('NFC');
  const canonical = normalized.trim();
  if (countUnicodeCodePoints(canonical) <= maxCodePoints) return normalized;
  return Array.from(canonical).slice(0, maxCodePoints).join('');
}

export function isSegmentComplete(
  segment: Pick<
    ReasonSegment,
    | 'reason_code'
    | 'reason_note'
    | 'committed_reason_code'
    | 'committed_reason_note'
    | 'reason_codes'
    | 'reason_notes'
    | 'committed_reason_codes'
    | 'committed_reason_notes'
  >,
  selectableCodes?: ReadonlySet<ReasonCode>,
  useCommittedFields = false
): boolean {
  const committedReasonCodes =
    segment.committed_reason_codes && segment.committed_reason_codes.length > 0
      ? segment.committed_reason_codes
      : segment.committed_reason_code
        ? [segment.committed_reason_code]
        : segment.reason_codes && segment.reason_codes.length > 0
          ? segment.reason_codes
          : segment.reason_code
            ? [segment.reason_code]
            : [];
  const committedReasonNotes =
    segment.committed_reason_notes && Object.keys(segment.committed_reason_notes).length > 0
      ? segment.committed_reason_notes
      : segment.committed_reason_note
        ? { other: segment.committed_reason_note }
        : segment.reason_notes && Object.keys(segment.reason_notes).length > 0
          ? segment.reason_notes
          : segment.reason_note
            ? { other: segment.reason_note }
            : {};
  const reasonCodes = useCommittedFields
    ? committedReasonCodes
    : (segment.reason_codes ?? (segment.reason_code ? [segment.reason_code] : []));
  const reasonNotes = useCommittedFields
    ? committedReasonNotes
    : (segment.reason_notes ?? (segment.reason_note ? { other: segment.reason_note } : {}));
  if (reasonCodes.length === 0 || new Set(reasonCodes).size !== reasonCodes.length) return false;
  if (useCommittedFields) return true;
  if (selectableCodes && reasonCodes.some((reasonCode) => !selectableCodes.has(reasonCode))) {
    return false;
  }
  if (!reasonCodes.includes(OTHER_REASON_CODE)) return true;
  const canonicalNote = canonicalizeReasonNote(reasonNotes[OTHER_REASON_CODE] ?? '');
  const noteLength = countUnicodeCodePoints(canonicalNote);
  return (
    noteLength >= 1 &&
    noteLength <= MAX_REASON_NOTE_CODE_POINTS &&
    hasVisibleReasonNoteContent(canonicalNote)
  );
}

export function countCompletedSegments(
  segments: ReasonSegment[],
  selectableCodes?: ReadonlySet<ReasonCode>,
  useCommittedFields = false
): number {
  return segments.reduce(
    (count, segment) =>
      count + (isSegmentComplete(segment, selectableCodes, useCommittedFields) ? 1 : 0),
    0
  );
}

export function shouldUseCommittedSegmentState(
  task: Pick<ReasonTaskSummary, 'first_committed_at_ms' | 'last_committed_revision' | 'revision'>,
  hasPendingDrafts = false
): boolean {
  if (hasPendingDrafts || task.first_committed_at_ms == null) return false;
  return task.last_committed_revision != null && task.revision === task.last_committed_revision;
}

export function getPreviewUrl(task: Pick<ReasonTaskSummary, 'episode_id' | 'preview_url'>): string {
  return (
    task.preview_url ??
    `https://cappreview.maxinsights.work/${encodeURIComponent(task.episode_id)}/${encodeURIComponent(task.episode_id)}.preview.mp4`
  );
}
